import type { TFunction } from "i18next";
import {
  Alert02Icon,
  BankIcon,
  CreditCardIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import type { ProgramInsight } from "components/common/ProgramInsightsPanel";
import type { PlatformMetrics } from "components/platform/dashboard/types";
import { formatCash } from "utils/formatCash";
import { formatNumber } from "utils/formatNumber";

/** Below this share of completed transactions, the ones that did not complete are worth a look. */
const HEALTH_WARNING_PERCENT = 95;

/** Past-due money as a whole percent of the active balance. */
export const pastDueShare = (pastDue: number, activeBalance: number): number =>
  activeBalance ? Math.floor((pastDue / activeBalance) * 100) : 0;

/**
 * What a platform operator can act on, worst first, from the metrics the
 * dashboard already fetches. No row links anywhere yet: Accounts shows neither
 * balances nor card counts, so it cannot show what any of them counts.
 */
export const platformInsights = (
  { funds, cards, transactions }: PlatformMetrics,
  t: TFunction,
): ProgramInsight[] => {
  const found: ProgramInsight[] = [];

  if (funds.pastDue > 0) {
    found.push({
      id: "past-due",
      severity: "critical",
      icon: BankIcon,
      wrapper: "danger",
      title: t("{{amount}} past due", {
        amount: formatCash(funds.pastDue) ?? "",
      }),
      details: [
        t("{{percent}}% of active balance", {
          percent: pastDueShare(funds.pastDue, funds.activeBalance),
        }),
      ],
    });
  }

  const incomplete =
    transactions.totalTransactions - transactions.completedTransactions;
  if (incomplete > 0 && transactions.healthPercent < HEALTH_WARNING_PERCENT) {
    found.push({
      id: "health",
      severity: "warning",
      icon: Alert02Icon,
      wrapper: "warning",
      title:
        incomplete === 1
          ? t("1 transaction did not complete")
          : t("{{number}} transactions did not complete", {
              number: formatNumber(incomplete),
            }),
      details: [
        t("Health score {{percent}}%", {
          percent: transactions.healthPercent,
        }),
      ],
    });
  }

  if (cards.lastMonth > 0 && cards.thisMonth !== cards.lastMonth) {
    const percent = formatNumber(
      Math.round(
        Math.abs((cards.thisMonth - cards.lastMonth) / cards.lastMonth) * 100,
      ),
    );
    found.push({
      id: "issuance",
      severity: "info",
      icon: CreditCardIcon,
      wrapper: "neutral",
      title:
        cards.thisMonth > cards.lastMonth
          ? t("Card issuance up {{percent}}%", { percent })
          : t("Card issuance down {{percent}}%", { percent }),
      details: [
        t("{{number}} issued this month", {
          number: formatNumber(cards.thisMonth),
        }),
      ],
    });
  }

  return found;
};
